import {
  Box,
  Checkbox,
  Text,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { TrainingsTrainingRequestScheduleEnum } from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import MoneyField from 'modules/core/components/MoneyField'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from '../types'

type Schedule = {
  label: string
  value: string
}
type Props = {
  amplitudeEventNext: string
}

export const TimingAndCostStep = ({
  data,
  onDataChanged,
  amplitudeEventNext,
}: WizardStepRenderParams<WizardData> & Props) => {
  const { employee, company } = useCurrentSession()
  const scheduleOptions: Schedule[] = [
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.during_work_hours'),
      value: 'workinghours',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.mixed_working_hours'),
      value: 'mixedhours',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.not_sure'),
      value: 'notsure',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.outside_working_hours'),
      value: 'outsideworkinghours',
    },
  ]

  useOnNext(() => {
    track(amplitudeEventNext, {
      from: 'my_request',
    })
  })

  return (
    <Box width="s600" gap="s24">
      <Box gap="s16">
        <Text size="400" weight="medium" color="grey1000">
          {i18n.t('trainings.training_requests.create.timing_cost_step.title')}
        </Text>
        <Text size="300" weight="normal" color="grey1000">
          {i18n.t('trainings.training_requests.create.timing_cost_step.description')}
        </Text>
      </Box>

      <DayPicker
        label={i18n.t('trainings.training_requests.create.dates')}
        value={[data.startDate, data.endDate]}
        mode="range"
        onChange={(value) => onDataChanged({ ...data, startDate: value[0], endDate: value[1] })}
        placeholder={'from - to'}
      />
      <Box gap="s4">
        <Text size="200" weight="medium" color="grey1000">
          {i18n.t('trainings.training_requests.create.training_hours')}
        </Text>
        <Box flexDirection="row" gap="s8">
          <TextField
            type="number"
            placeholder={i18n.t('trainings.training_requests.create.duration_hours_placeholder')}
            onChange={(value) => onDataChanged({ ...data, duration: Number(value) })}
          />
          <TextField
            type="number"
            placeholder={i18n.t('trainings.training_requests.create.duration_minutes_placeholder')}
            onChange={(value) => onDataChanged({ ...data, minutes: Number(value) })}
          />
        </Box>
      </Box>
      <Selector.Popover
        label={i18n.t('trainings.training_requests.create.training_schedule')}
        placeholder={i18n.t('trainings.training_requests.create.select_placeholder')}
        searchPlaceholder={i18n.t('common.search')}
        blankLabel=""
        value={[data.schedule]}
        onChange={(selection) =>
          onDataChanged({ ...data, schedule: selection as TrainingsTrainingRequestScheduleEnum })
        }
      >
        {scheduleOptions.map((option, index) => (
          <Selector.Option
            key={index}
            value={option.value}
            label={option.label}
            searchValue={option.label}
          >
            {option.label}
          </Selector.Option>
        ))}
      </Selector.Popover>
      <Box width="fit-content">
        <MoneyField
          label={i18n.t('trainings.training_requests.create.cost')}
          currency={employee?.legalEntity?.currency ?? ''}
          value={data.cost ? String(data.cost) : ''}
          placeholder="0"
          onChange={(value) => onDataChanged({ ...data, cost: Number(value) })}
        />
      </Box>
      <Box flexDirection="row" gap="s8" alignItems="center">
        <Checkbox
          checked={data.subsidized}
          onChange={() => onDataChanged({ ...data, subsidized: !data.subsidized })}
          size="large"
        />
        <Text size="200" weight="medium" color="grey800">
          {i18n.t('trainings.training_requests.create.subsidized_checkbox_text')}
        </Text>
      </Box>
    </Box>
  )
}
